// WP-1.6 (M7 FX-1..3): indicative FX rate queries/mutations. A missing cached rate is
// normal (nothing set yet), not an error — surfaced as `data: undefined`, not a thrown query.
import type { CurrencyCode } from '@pfos/engine';
import { fxRateResponseSchema, type FxRateResponse } from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, ApiError } from './api';

export const FX_RATE_KEY = ['fx', 'rate'] as const;

export function useFxRate(
  from: CurrencyCode | undefined,
  to: CurrencyCode | undefined,
): UseQueryResult<FxRateResponse | null> {
  return useQuery({
    queryKey: [...FX_RATE_KEY, from, to],
    enabled: from !== undefined && to !== undefined && from !== to,
    queryFn: async () => {
      if (from === undefined || to === undefined) throw new Error('unreachable: query is disabled');
      try {
        return fxRateResponseSchema.parse(await api.get(`/api/v1/fx/rates/${from}/${to}`));
      } catch (e) {
        // React Query rejects `undefined` query results — null means "confirmed no cached rate".
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
}

export function useSetFxRate(): UseMutationResult<
  FxRateResponse,
  Error,
  { from: CurrencyCode; to: CurrencyCode; rate: number }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      fxRateResponseSchema.parse(await api.post('/api/v1/fx/rates', input)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: FX_RATE_KEY });
    },
  });
}
